import React, { useMemo, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Fulfillment } from "@secondcloset/types";
import { Button, Checkbox, InputNumber, Modal, Space, Table, Typography } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { useMutation } from "react-query";
import { createShipment } from "../../api/fulfillment/shipment";

type OrderItem = Fulfillment.OrderItem<"V2">;
interface CreateUntrackedShipmentButtonProps {
  orderId: string;
  orderItems: OrderItem[];
  onRefetchOrder: () => void;
}
export const CreateUntrackedShipmentButton: React.FC<CreateUntrackedShipmentButtonProps> = ({
  orderId,
  orderItems,
  onRefetchOrder,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [willConsumeStock, setWillConsumeStock] = useState(true);
  const [shipmentNotes, setShipmentNotes] = useState("");
  const [products, setProducts] = useState<Record<string, number>>({});

  const orderItemsToBeDeliver = useMemo(
    () => orderItems.filter((item) => item.available_actions?.delivery_item_ids?.length > 0),
    [orderItems]
  );

  const { mutate: doCreateShipment, ...shipment } = useMutation(createShipment, {
    onSuccess: () => {
      setIsModalVisible(false);
      onRefetchOrder();
    },
  });

  const handleCreateUntrackedShipments = () => {
    const items = Object.entries(products).flatMap(([productId, quantity]) => ({
      product_id: productId,
      quantity: quantity,
    }));

    return doCreateShipment({
      version: 2,
      allow_partial_fulfillment: true,
      order_id: orderId,
      items: items,
      untracked: true,
      consume_stock: willConsumeStock,
      job_type: "delivery",
      notes_attributes: [{ text: shipmentNotes.trim() }],
    });
  };

  const columns = [
    {
      title: "Product",
      dataIndex: "product",
      render: (product: OrderItem["product"]) => {
        return (
          <Space direction="vertical" size="small">
            <Typography.Link>{product.name}</Typography.Link>
            <Typography.Text>SKU: {product.sku}</Typography.Text>
          </Space>
        );
      },
    },
    {
      title: "Quantity",
      dataIndex: ["available_actions", "delivery_item_ids"],
      render: (deliveryItems: OrderItem["available_actions"]["delivery_item_ids"], { product }: OrderItem) => {
        const max = deliveryItems?.length || 0;
        return (
          <Space>
            <InputNumber
              min={0}
              max={max}
              value={products[product.id]}
              onChange={(value) => setProducts((state) => ({ ...state, [product.id]: value || 0 }))}
            />
            <Typography.Text>/{max}</Typography.Text>
          </Space>
        );
      },
    },
    {
      title: "Available Stock",
      dataIndex: ["product", "available_quantity"],
    },
  ];

  return (
    <>
      <Button icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
        Create Untracked Delivery Shipment
      </Button>
      <Modal
        visible={isModalVisible}
        title="Create Untracked Delivery Shipment"
        onCancel={() => setIsModalVisible(false)}
        onOk={handleCreateUntrackedShipments}
        okButtonProps={{
          disabled: Object.values(products).every((quantity) => quantity === 0),
          loading: shipment.isLoading,
        }}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Table
            dataSource={orderItemsToBeDeliver}
            columns={columns}
            pagination={false}
            rowKey={(item) => item.product.id}
          />
          <Checkbox checked={willConsumeStock} onClick={() => setWillConsumeStock(!willConsumeStock)}>
            Consume Stock
          </Checkbox>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text>Shipment Notes</Typography.Text>
            <TextArea
              value={shipmentNotes}
              onChange={(e) => setShipmentNotes(e.currentTarget.value)}
              placeholder="Please enter reference or tracking number if possible"
              allowClear
            />
          </Space>
        </Space>
      </Modal>
    </>
  );
};
